import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity, AlertCircle, CalendarClock, CheckCircle2, CircleDollarSign, Clock,
  FileText, FolderKanban, Hourglass, ListChecks, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ACTION_LABELS, api, eur, errMsg, fmtDateShort, fmtDateTime, STATUS } from "../lib/api";
import { PayBadge, StatusBadge, UserAvatar } from "../components/ui-bits";

const STAT_CARDS = [
  { key: "total", label: "Projekte gesamt", icon: FolderKanban, testid: "stat-projekte-gesamt" },
  { key: "aktiv", label: "Aktiv", icon: Activity, testid: "stat-aktiv" },
  { key: "wartet_kunde", label: "Wartet auf Kunde", icon: Hourglass, testid: "stat-wartet-kunde" },
  { key: "wartet_uns", label: "Wartet auf uns", icon: AlertCircle, testid: "stat-wartet-uns" },
  { key: "abgeschlossen", label: "Abgeschlossen", icon: CheckCircle2, testid: "stat-abgeschlossen" },
  { key: "open_payments", label: "Offen an Mondher", icon: CircleDollarSign, currency: true, testid: "stat-offene-zahlungen" },
];

function Widget({ title, icon: Icon, children, testid, action }) {
  return (
    <section className="rounded-2xl border border-[#1D225C]/10 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-sm font-semibold text-[#1D225C]">
          <Icon className="h-4 w-4 text-[#B88A4A]" /> {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6" data-testid="dashboard-loading">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-28 animate-pulse rounded-2xl bg-white" />
        ))}
      </div>
    );
  }

  const { stats } = data;
  const hour = new Date().getHours();
  const greeting = hour < 11 ? "Guten Morgen" : hour < 18 ? "Guten Tag" : "Guten Abend";

  const completeTask = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}/status`, { status: "fertig" });
      toast.success(`„${t.title}" abgeschlossen`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B88A4A]">Control Center</p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#1D225C] sm:text-3xl" data-testid="dashboard-greeting">
          {greeting}, {user.name.split(" ")[0]}
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-6">
        {STAT_CARDS.map((c) => (
          <div
            key={c.key}
            data-testid={c.testid}
            className="rounded-2xl border border-[#1D225C]/10 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
          >
            <c.icon className="mb-2 h-5 w-5 text-[#B88A4A]" />
            <p className="font-display text-2xl font-bold text-[#1D225C]">
              {c.currency ? eur(stats[c.key]) : stats[c.key]}
            </p>
            <p className="mt-0.5 text-xs font-medium text-[#777]">{c.label}</p>
          </div>
        ))}
      </div>

      {stats.total === 0 && (
        <div className="rounded-2xl border border-[#B88A4A]/30 bg-[#B88A4A]/5 p-6" data-testid="dashboard-getting-started">
          <h3 className="font-display text-lg font-semibold text-[#1D225C]">Willkommen im Control Center</h3>
          <p className="mt-1 text-sm text-[#555]">
            So startest du: 1. <Link to="/kunden" className="font-semibold text-[#B88A4A] hover:underline">Kunden anlegen</Link> →
            2. Projekt erstellen → 3. Aufgaben zuweisen, Dateien hochladen, Zugänge hinterlegen.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Widget
          title="Meine Aufgaben"
          icon={ListChecks}
          testid="widget-my-tasks"
          action={<Link to="/aufgaben" className="text-xs font-medium text-[#B88A4A] hover:underline">Alle ansehen</Link>}
        >
          {data.my_tasks.length === 0 ? (
            <p className="py-4 text-center text-sm text-[#777]">Keine offenen Aufgaben für dich.</p>
          ) : (
            <ul className="divide-y divide-[#1D225C]/5">
              {data.my_tasks.map((t) => (
                <li key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`dashboard-task-${t.id}`}>
                  <button
                    data-testid={`dashboard-task-complete-${t.id}`}
                    onClick={() => completeTask(t)}
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 border-[#1D225C]/20 transition-colors hover:border-[#22C55E] hover:bg-[#22C55E]/10"
                    title="Als erledigt markieren"
                  />
                  <button onClick={() => navigate(`/projekte/${t.project_id}?tab=aufgaben`)} className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-medium text-[#1D225C]">{t.title}</p>
                    <p className="truncate text-xs text-[#777]">{t.client_name} · {t.project_name}</p>
                  </button>
                  {t.due_date && (
                    <span className="flex items-center gap-1 text-xs text-[#B88A4A]">
                      <Clock className="h-3 w-3" /> {fmtDateShort(t.due_date)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Widget>

        <Widget title="Bevorstehende Fristen" icon={CalendarClock} testid="widget-deadlines" action={<Link to="/kalender" className="text-xs font-medium text-[#B88A4A] hover:underline">Kalender</Link>}>
          {data.upcoming_deadlines.length === 0 ? (
            <p className="py-4 text-center text-sm text-[#777]">Keine Fristen in den nächsten 14 Tagen.</p>
          ) : (
            <ul className="divide-y divide-[#1D225C]/5">
              {data.upcoming_deadlines.map((d) => (
                <li key={d.kind + d.id}>
                  <button onClick={() => navigate(d.link)} className="flex w-full items-center gap-3 py-2.5 text-left" data-testid={`dashboard-deadline-${d.id}`}>
                    <span className={`rounded-lg px-2 py-1 text-[10px] font-bold uppercase ${d.kind === "project" ? "bg-[#B88A4A]/10 text-[#B88A4A]" : "bg-blue-50 text-blue-600"}`}>
                      {d.kind === "project" ? "Projekt" : "Aufgabe"}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#1D225C]">{d.title}</span>
                    <span className="text-xs text-[#777]">{fmtDateShort(d.date)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        <Widget title="Wartet auf Kunde" icon={Hourglass} testid="widget-waiting-client" action={<Link to="/projekte" className="text-xs font-medium text-[#B88A4A] hover:underline">Projekte</Link>}>
          {data.waiting_client.length === 0 ? (
            <p className="py-4 text-center text-sm text-[#777]">Nichts wartet auf Kunden.</p>
          ) : (
            <ul className="divide-y divide-[#1D225C]/5">
              {data.waiting_client.map((p) => (
                <li key={p.id}>
                  <button onClick={() => navigate(`/projekte/${p.id}`)} className="flex w-full items-center gap-3 py-2.5 text-left" data-testid={`dashboard-waiting-${p.id}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[#1D225C]">{p.name}</span>
                      <span className="block truncate text-xs text-[#777]">{p.client_name}</span>
                    </span>
                    <StatusBadge status={p.status} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        <Widget title="Offene Zahlungen an Mondher" icon={Wallet} testid="widget-open-payments" action={<Link to="/zahlungen" className="text-xs font-medium text-[#B88A4A] hover:underline">Zahlungen</Link>}>
          {data.open_payments_rows.length === 0 ? (
            <p className="py-4 text-center text-sm text-[#777]">Alles bezahlt. 🎉</p>
          ) : (
            <ul className="divide-y divide-[#1D225C]/5">
              {data.open_payments_rows.map((r) => (
                <li key={r.project_id}>
                  <button onClick={() => navigate(`/projekte/${r.project_id}?tab=zahlungen`)} className="flex w-full items-center gap-3 py-2.5 text-left" data-testid={`dashboard-payment-${r.project_id}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[#1D225C]">{r.project_name}</span>
                      <span className="block truncate text-xs text-[#777]">{r.client_name}</span>
                    </span>
                    <PayBadge status={r.payment_status} />
                    <span className="font-display text-sm font-bold text-[#B88A4A]">{eur(r.open)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        <Widget title="Letzte Aktivität" icon={Activity} testid="widget-activity" action={<Link to="/aktivitaet" className="text-xs font-medium text-[#B88A4A] hover:underline">Alle</Link>}>
          {data.recent_activity.length === 0 ? (
            <p className="py-4 text-center text-sm text-[#777]">Noch keine Aktivitäten.</p>
          ) : (
            <ul className="space-y-2.5">
              {data.recent_activity.map((a) => (
                <li key={a.id} className="flex gap-2.5 text-sm" data-testid={`dashboard-activity-${a.id}`}>
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#B88A4A]" />
                  <div className="min-w-0">
                    <p className="leading-snug text-[#1D225C]">
                      <span className="font-semibold">{a.user_name}</span> · {ACTION_LABELS[a.action] || a.action}
                    </p>
                    <p className="truncate text-xs text-[#777]">{a.details}</p>
                    <p className="text-[11px] text-[#999]">{fmtDateTime(a.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        <Widget title="Zuletzt hochgeladene Dateien" icon={FileText} testid="widget-recent-files" action={<Link to="/dateien" className="text-xs font-medium text-[#B88A4A] hover:underline">Dateien</Link>}>
          {data.recent_files.length === 0 ? (
            <p className="py-4 text-center text-sm text-[#777]">Noch keine Dateien hochgeladen.</p>
          ) : (
            <ul className="divide-y divide-[#1D225C]/5">
              {data.recent_files.map((f) => (
                <li key={f.id}>
                  <button onClick={() => navigate(`/projekte/${f.project_id}?tab=dateien`)} className="flex w-full items-center gap-3 py-2.5 text-left" data-testid={`dashboard-file-${f.id}`}>
                    <FileText className="h-4 w-4 shrink-0 text-[#1D225C]/40" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[#1D225C]">{f.original_name}</span>
                      <span className="block truncate text-xs text-[#777]">{f.project_name} · {f.uploader_name}</span>
                    </span>
                    <span className="text-xs text-[#777]">{fmtDateShort(f.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Widget>
      </div>

      {data.recent_projects.length > 0 && (
        <section data-testid="widget-recent-projects">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold text-[#1D225C]">Aktuelle Projekte</h3>
            <Link to="/projekte" className="text-xs font-medium text-[#B88A4A] hover:underline">Alle Projekte</Link>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {data.recent_projects.map((p) => (
              <button
                key={p.id}
                data-testid={`dashboard-project-${p.id}`}
                onClick={() => navigate(`/projekte/${p.id}`)}
                className="rounded-2xl border border-[#1D225C]/10 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <p className="truncate text-xs font-medium text-[#B88A4A]">{p.client_name}</p>
                <p className="mt-0.5 truncate font-display text-sm font-semibold text-[#1D225C]">{p.name}</p>
                <div className="mt-2"><StatusBadge status={p.status} /></div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#1D225C]/10">
                  <div className="h-full rounded-full bg-[#B88A4A] transition-all" style={{ width: `${p.progress}%` }} />
                </div>
                <p className="mt-1 text-[11px] text-[#777]">{p.progress}% · {p.task_done}/{p.task_total} Aufgaben</p>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
